import {ReactNode} from 'react';

export interface TracingGridRowRaw {
    averagePrice: string;
    gtin: string;
    quantity: string;
    group: string;
    product: {
        gtin: string;
        name: string;
        brand: string;
        group: string;
    };
    seller: {
        clientId: number;
        name: string;
        inn: string;
    };
}

export enum TracingGridAccessors {
    id = 'id',
    name = 'name',
    gtin = 'gtin',
    trademark = 'brand',
    codeGroup = 'group',
    qnt = 'quantity',
    avgPrice = 'averagePrice',
    seller = 'seller',
    manufacturer = 'manufacturer',
}

export interface TracingGridFormattedRow {
    [TracingGridAccessors.seller]?: string;
    [TracingGridAccessors.name]: ReactNode;
    [TracingGridAccessors.gtin]: string;
    [TracingGridAccessors.trademark]: string;
    [TracingGridAccessors.codeGroup]: string;
    [TracingGridAccessors.qnt]: string;
    [TracingGridAccessors.avgPrice]: string;
}

export interface TracingNodeProperties {
    id: string;
    localId: string;
    inn: string;
    name: string;
    gtin: string;
    childrenCount: number;
}

export interface TracingRelationshipProperties {
    costVatFree: string;
    costWithVat: string;
    gtin: string;
    price: string;
    quantity: string;
    updDate: string;
    markingCode: string;
}

export type TracingNode = TracingNodeProperties & {
    inputAmount: number;
    outputAmount: number;
    startAmount: number;
    sum: number;
    soldAmount: number;
    dropoutAmount: number;
    isSold: boolean;
    isDropout: boolean;
    children?: TracingNode[];
    parents?: TracingNode[];
    clientId?: string;
};

export interface TracingDetailRaw {
    id: string;
    averagePrice: string;
    gtin: string;
    manufacturer: {
        clientId: number;
        name: string;
        inn: string;
    };
    name: string;
    brand: string;
    quantity: string;
    productGroup: string;
    producedQuantity: number;
    startQuantity: number;
    root: TracingNodeProperties;
}

export interface TracingDetailFormatted {
    id: string;
    name: string;
    gtin: string;
    trademark: string;
    codeGroup: string;
    qnt: string;
    avgPrice: string;
    manufacturer: string;
}

export interface TracingGraphRaw {
    name: string;
    sum: number;
    amount: number;
    date: string;
    link: string;
    children?: TracingGraphRaw[];
}

export interface TracingGraphFormatted {
    name: string;
    sum: string;
    amount: string;
    date: string;
    link: string;
    children?: TracingGraphFormatted[];
    rels?: TracingRelationshipProperties[];
}

export interface TracingNodeStatement {
    label: string;
    transform: (d: TracingNode) => any;
    showIf?: (d: TracingNode) => boolean;
}

export type RecursivePartial<T> = {
    [P in keyof T]?: T[P] extends (infer U)[]
        ? RecursivePartial<U>[]
        : T[P] extends object | undefined
        ? RecursivePartial<T[P]>
        : T[P];
};

export interface TraceGraphConfig {
    expandAll?: boolean;
    disableToggle?: boolean;
    disableYMove?: boolean;
    disableXMove?: boolean;
    disableZoom: boolean;
    initialZoom: number;
    heightFixed: boolean;
    width: number;
    height: number;
    limit?: number;
    margin: {top: number; right: number; left: number; bottom: number};
    card: {
        width: number;
        height: number;
        padding: {
            x: number;
            y: number;
        };
        icon: {
            size: number;
            rightGap: number;
            borderRadius: number;
        };
        toggle: {
            size: number;
        };
        borderRadius: number;
        verticalSpace: number;
        horizontalSpace: number;
    };
    color: {
        iconBg: string;
        main: {
            default: string;
            active: string;
        };
    };
}

export type OverrideTraceGraphConfig = RecursivePartial<TraceGraphConfig>;
